// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * Theme Color Context
 *
 * Manages user preference for the application theme/header color and AI button color.
 * Provides preset healthcare-appropriate color options with shade variations.
 *
 * Preferences are persisted to localStorage (no PHI involved).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { JSX, ReactNode } from 'react';

const STORAGE_KEY = 'carepoint-theme-color';
const AI_BUTTON_STORAGE_KEY = 'carepoint-ai-button-color';

export interface ColorShade {
  light: string;
  medium: string;
  dark: string;
}

export interface ThemeColorGroup {
  id: string;
  name: string;
  shades: ColorShade;
}

// Organized color palette with light/medium/dark shades
export const THEME_COLOR_GROUPS: ThemeColorGroup[] = [
  {
    id: 'black',
    name: 'Black',
    shades: { light: '#374151', medium: '#1f2937', dark: '#111827' },
  },
  {
    id: 'plum',
    name: 'Plum',
    shades: { light: '#7a5568', medium: '#553a48', dark: '#3d2a34' },
  },
  {
    id: 'navy',
    name: 'Navy',
    shades: { light: '#4a789d', medium: '#3d5a80', dark: '#2f4460' },
  },
  {
    id: 'slate',
    name: 'Slate',
    shades: { light: '#64748b', medium: '#475569', dark: '#334155' },
  },
  {
    id: 'gray',
    name: 'Gray',
    shades: { light: '#6b7280', medium: '#4b5563', dark: '#374151' },
  },
  {
    id: 'zinc',
    name: 'Zinc',
    shades: { light: '#71717a', medium: '#52525b', dark: '#3f3f46' },
  },
  {
    id: 'stone',
    name: 'Stone',
    shades: { light: '#78716c', medium: '#57534e', dark: '#44403c' },
  },
  {
    id: 'red',
    name: 'Red',
    shades: { light: '#ef4444', medium: '#dc2626', dark: '#b91c1c' },
  },
  {
    id: 'orange',
    name: 'Orange',
    shades: { light: '#f97316', medium: '#ea580c', dark: '#c2410c' },
  },
  {
    id: 'amber',
    name: 'Amber',
    shades: { light: '#f59e0b', medium: '#d97706', dark: '#b45309' },
  },
  {
    id: 'yellow',
    name: 'Yellow',
    shades: { light: '#eab308', medium: '#ca8a04', dark: '#a16207' },
  },
  {
    id: 'lime',
    name: 'Lime',
    shades: { light: '#84cc16', medium: '#65a30d', dark: '#4d7c0f' },
  },
  {
    id: 'green',
    name: 'Green',
    shades: { light: '#22c55e', medium: '#16a34a', dark: '#15803d' },
  },
  {
    id: 'emerald',
    name: 'Emerald',
    shades: { light: '#10b981', medium: '#059669', dark: '#047857' },
  },
  {
    id: 'teal',
    name: 'Teal',
    shades: { light: '#14b8a6', medium: '#0d9488', dark: '#0f766e' },
  },
  {
    id: 'cyan',
    name: 'Cyan',
    shades: { light: '#06b6d4', medium: '#0891b2', dark: '#0e7490' },
  },
  {
    id: 'sky',
    name: 'Sky',
    shades: { light: '#0ea5e9', medium: '#0284c7', dark: '#0369a1' },
  },
  {
    id: 'blue',
    name: 'Blue',
    shades: { light: '#3b82f6', medium: '#2563eb', dark: '#1d4ed8' },
  },
  {
    id: 'indigo',
    name: 'Indigo',
    shades: { light: '#6366f1', medium: '#4f46e5', dark: '#4338ca' },
  },
  {
    id: 'violet',
    name: 'Violet',
    shades: { light: '#8b5cf6', medium: '#7c3aed', dark: '#6d28d9' },
  },
  {
    id: 'purple',
    name: 'Purple',
    shades: { light: '#a855f7', medium: '#9333ea', dark: '#7e22ce' },
  },
  {
    id: 'fuchsia',
    name: 'Fuchsia',
    shades: { light: '#d946ef', medium: '#c026d3', dark: '#a21caf' },
  },
  {
    id: 'pink',
    name: 'Pink',
    shades: { light: '#ec4899', medium: '#db2777', dark: '#be185d' },
  },
  {
    id: 'rose',
    name: 'Rose',
    shades: { light: '#f43f5e', medium: '#e11d48', dark: '#be123c' },
  },
];

// AI Button color options
export const AI_BUTTON_COLORS = [
  // Gradients
  { id: 'gradient-default', name: 'Default (Blue-Teal)', color: 'linear-gradient(135deg, #0063b8 0%, #08a88a 100%)' },
  { id: 'gradient-purple', name: 'Purple Gradient', color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { id: 'gradient-blue', name: 'Blue Gradient', color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
  { id: 'gradient-teal', name: 'Teal Gradient', color: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' },
  { id: 'gradient-sunset', name: 'Sunset Gradient', color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { id: 'gradient-gold', name: 'Gold Gradient', color: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)' },
  { id: 'gradient-ocean', name: 'Ocean Gradient', color: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)' },
  { id: 'gradient-emerald', name: 'Emerald Gradient', color: 'linear-gradient(135deg, #11998e 0%, #059669 100%)' },
  { id: 'gradient-rose', name: 'Rose Gradient', color: 'linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%)' },
  { id: 'gradient-midnight', name: 'Midnight Gradient', color: 'linear-gradient(135deg, #232526 0%, #414345 100%)' },
  // Solid colors
  { id: 'solid-black', name: 'Black', color: '#1a1a1a' },
  { id: 'solid-charcoal', name: 'Charcoal', color: '#374151' },
  { id: 'solid-purple', name: 'Purple', color: '#7c3aed' },
  { id: 'solid-blue', name: 'Blue', color: '#2563eb' },
  { id: 'solid-teal', name: 'Teal', color: '#0d9488' },
  { id: 'solid-green', name: 'Green', color: '#16a34a' },
  { id: 'solid-pink', name: 'Pink', color: '#db2777' },
  { id: 'solid-indigo', name: 'Indigo', color: '#4f46e5' },
  { id: 'solid-red', name: 'Red', color: '#dc2626' },
  { id: 'solid-orange', name: 'Orange', color: '#ea580c' },
];

export interface ThemeColorOption {
  groupId: string;
  shade: 'light' | 'medium' | 'dark';
  color: string;
}

export interface AIButtonOption {
  id: string;
  name: string;
  color: string;
}

// Default colors
const DEFAULT_THEME: ThemeColorOption = {
  groupId: 'plum',
  shade: 'medium',
  color: '#553a48',
};

const DEFAULT_AI_BUTTON: AIButtonOption = AI_BUTTON_COLORS[0];

interface ThemeColorContextValue {
  /** Current theme color */
  themeColor: ThemeColorOption;
  /** Set the theme color */
  setThemeColor: (groupId: string, shade: 'light' | 'medium' | 'dark') => void;
  /** Get all available color groups */
  colorGroups: ThemeColorGroup[];
  /** Current AI button color */
  aiButtonColor: AIButtonOption;
  /** Set the AI button color */
  setAIButtonColor: (colorId: string) => void;
  /** Get all available AI button colors */
  aiButtonOptions: AIButtonOption[];
}

const ThemeColorContext = createContext<ThemeColorContextValue | undefined>(undefined);

interface ThemeColorProviderProps {
  readonly children: ReactNode;
}

export function ThemeColorProvider({ children }: ThemeColorProviderProps): JSX.Element {
  const [themeColor, setThemeColorState] = useState<ThemeColorOption>(DEFAULT_THEME);
  const [aiButtonColor, setAIButtonColorState] = useState<AIButtonOption>(DEFAULT_AI_BUTTON);

  // Load preferences from localStorage on mount
  useEffect(() => {
    // Load theme color
    const savedTheme = localStorage.getItem(STORAGE_KEY);
    if (savedTheme) {
      try {
        const parsed = JSON.parse(savedTheme) as { groupId: string; shade: 'light' | 'medium' | 'dark' };
        const group = THEME_COLOR_GROUPS.find((g) => g.id === parsed.groupId);
        if (group) {
          setThemeColorState({
            groupId: parsed.groupId,
            shade: parsed.shade,
            color: group.shades[parsed.shade],
          });
        }
      } catch {
        // If old format (just color id), try to migrate
        const group = THEME_COLOR_GROUPS.find((g) => g.id === savedTheme);
        if (group) {
          setThemeColorState({
            groupId: savedTheme,
            shade: 'medium',
            color: group.shades.medium,
          });
        }
      }
    }

    // Load AI button color
    const savedAIButton = localStorage.getItem(AI_BUTTON_STORAGE_KEY);
    if (savedAIButton) {
      const found = AI_BUTTON_COLORS.find((c) => c.id === savedAIButton);
      if (found) {
        setAIButtonColorState(found);
      }
    }
  }, []);

  // Apply CSS custom properties when theme color changes
  useEffect(() => {
    const root = document.documentElement;
    const group = THEME_COLOR_GROUPS.find((g) => g.id === themeColor.groupId);
    if (group) {
      root.style.setProperty('--carepoint-theme-color', themeColor.color);
      root.style.setProperty('--carepoint-theme-color-light', group.shades.light);
      root.style.setProperty('--carepoint-theme-color-dark', group.shades.dark);
    }
  }, [themeColor]);

  // Apply AI button color CSS property
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--carepoint-ai-button-color', aiButtonColor.color);
  }, [aiButtonColor]);

  const setThemeColor = useCallback((groupId: string, shade: 'light' | 'medium' | 'dark'): void => {
    const group = THEME_COLOR_GROUPS.find((g) => g.id === groupId);
    if (group) {
      const newTheme: ThemeColorOption = {
        groupId,
        shade,
        color: group.shades[shade],
      };
      setThemeColorState(newTheme);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ groupId, shade }));
    }
  }, []);

  const setAIButtonColor = useCallback((colorId: string): void => {
    const found = AI_BUTTON_COLORS.find((c) => c.id === colorId);
    if (found) {
      setAIButtonColorState(found);
      localStorage.setItem(AI_BUTTON_STORAGE_KEY, colorId);
    }
  }, []);

  const value = useMemo(
    (): ThemeColorContextValue => ({
      themeColor,
      setThemeColor,
      colorGroups: THEME_COLOR_GROUPS,
      aiButtonColor,
      setAIButtonColor,
      aiButtonOptions: AI_BUTTON_COLORS,
    }),
    [themeColor, setThemeColor, aiButtonColor, setAIButtonColor]
  );

  return <ThemeColorContext.Provider value={value}>{children}</ThemeColorContext.Provider>;
}

export function useThemeColor(): ThemeColorContextValue {
  const context = useContext(ThemeColorContext);
  if (!context) {
    throw new Error('useThemeColor must be used within a ThemeColorProvider');
  }
  return context;
}
